<template>
  <div>
    <l-button variant="light" class="gap-2 relative" :size="breakpoints.smAndDown ? 'sm' : 'md'" @click="openModal">
      <icon name="settings" set="regular" class="text-gray-400"/>
      Settings
      <icon v-if="progress < 100" name="circleWarning" class="rounded-full bg-white dark:bg-gray-900 text-yellow absolute z-10 -top-2 -right-2"/>
    </l-button>

    <modal
      title="Personal report settings"
      :show="showModal"
      scrollable
      size="3xl"
      @close="closeModal"
    >
      <modal-tabs v-bind="{ items }" class="h-full sm:min-h-[570px]">
        <template #default="{ item }">
          <h3 class="sr-only">
            {{ item.title }}
          </h3>
          <component :is="item.component" v-if="item" v-model="user"/>
        </template>

        <template #append-sidebar>
          <div v-if="progress < 100" class="pt-3 border-t mt-3">
            <div class="font-semibold text-blue dark:text-white mb-1">
              {{ progress }}% complete
            </div>
            <div class="rounded bg-gray-200 h-2 w-full overflow-hidden" aria-hidden="true">
              <div class="h-full bg-green-500" :style="{ width: `${progress}%` }"/>
            </div>
            <p class="text-gray-500 text-sm mt-2">
              Complete your personal report settings to see more personalized insights.
            </p>
          </div>
        </template>
      </modal-tabs>
    </modal>
  </div>
</template>

<script setup>
import DebtPaymentSettingsForm from "@/components/forms/DebtPaymentSettingsForm.vue"
import DiscountEligibilitySettingsForm from "@/components/forms/DiscountEligibilitySettingsForm.vue"
import Icon from "@/components/Icon.vue"
import JobPreferencesForm from "@/components/forms/JobPreferencesForm.vue"
import LButton from "@/components/buttons/LButton.vue"
import LocationPreferencesForm from "@/components/forms/LocationPreferencesForm.vue"
import LsatGpaSettingsForm from "@/components/forms/LsatGpaSettingsForm.vue"
import Modal from "@/components/modals/Modal.vue"
import ModalTabs from "@/components/ModalTabs.vue"
import RaceSettingsForm from "@/components/forms/RaceSettingsForm.vue"
import { breakpoints } from "@/breakpoints"
import { computed, ref } from "vue"
import { useJobs } from "@/composables/useJobs"
import { useRaces } from "@/composables/useRaces"

const user = defineModel({ type: Object, required: true })

defineProps({
  progress: Number,
})

const { races } = useRaces()

const { jobs } = useJobs()

const showModal = ref(false)

const closeModal = () => {
  showModal.value = false
}

const openModal = () => {
  showModal.value = true
}

const locationPreferences = computed(() => {
  let states = user.value.statesPreferred?.map((state) => state.code)

  if (user.value.report.national) {
    states = ["National", ...states]
  }

  return states.join(", ")
})

const items = computed(() => [
  {
    title: "Location",
    icon: "fa-location-arrow",
    component: LocationPreferencesForm,
    subtitle: locationPreferences.value,
  },
  {
    title: "Job priorities",
    icon: "fa-circle-nodes",
    component: JobPreferencesForm,
    subtitle: jobs?.filter(job => user.value.report[job.field] > 0).sort((a, b) => user.value.report[b.field] - user.value.report[a.field]).map(job => job.title).join(", "),
  },
  {
    title: "LSAT and GPA",
    icon: "fa-ranking-star",
    component: LsatGpaSettingsForm,
    subtitle: `${user.value.lsat || "Not set"} / ${user.value.gpa || "Not set"}`,
  },
  {
    title: "Race/ethnicity",
    icon: "fa-rectangle-history-circle-user",
    component: RaceSettingsForm,
    subtitle: races?.filter((race) => user.value.race.includes(race.value)).map(race => race.name).join(", "),
  },
  {
    title: "Debt payment",
    icon: "fa-hand-holding-circle-dollar",
    component: DebtPaymentSettingsForm,
    subtitle: `${user.value.finances.target_tax || 10}% for ${user.value.finances.target_debt_period} years`,
  },
  {
    title: "Discount eligibility",
    icon: "fa-chart-pie-simple-circle-dollar",
    component: DiscountEligibilitySettingsForm,
    subtitle: user.value.states?.map((state) => state.code).join(", "),
  },
])
</script>
